import { Injectable } from '@angular/core';

export class SelectionError extends Error {
    public constructor(public readonly reason: 'count' | 'read' | 'aborted') {
        super(reason);
    }
}

export interface SelectionOptions {
    strictMaxRawFileCount?: number;
    signal?: AbortSignal;
}

@Injectable()
export class Selection {
    public readFilesFromBrowse(
        input: HTMLInputElement,
        allowFolderSelection: boolean
    ): File[] {
        const files = Array.from(input.files ?? []);
        return allowFolderSelection
            ? files.filter((file): file is File => file instanceof File)
            : files;
    }

    public async extractFilesFromDrop(
        dataTransfer: DataTransfer,
        options: SelectionOptions = {}
    ): Promise<File[]> {
        this.checkAborted(options.signal);
        // Native drag data is only readable during the synchronous drop event.
        const fallbackFiles = Array.from(dataTransfer.files ?? []);
        const entries = Array.from(dataTransfer.items ?? []).map((item) => ({
            kind: item.kind,
            entry:
                typeof item.webkitGetAsEntry === 'function'
                    ? item.webkitGetAsEntry()
                    : null,
            file:
                typeof item.getAsFile === 'function' ? item.getAsFile() : null,
        }));
        const files: File[] = [];
        const append = (file: File) => {
            this.checkAborted(options.signal);
            files.push(file);
            if (
                options.strictMaxRawFileCount !== undefined &&
                files.length > options.strictMaxRawFileCount
            ) {
                throw new SelectionError('count');
            }
        };
        const visit = async (entry: FileSystemEntry): Promise<void> => {
            this.checkAborted(options.signal);
            if (entry.isDirectory && 'createReader' in entry) {
                const reader = (
                    entry as FileSystemDirectoryEntry
                ).createReader();
                while (true) {
                    const batch = await this.read<FileSystemEntry[]>(
                        (resolve, reject) =>
                            reader.readEntries(resolve, reject),
                        options.signal
                    );
                    if (!batch.length) break;
                    for (const child of batch) await visit(child);
                }
            } else if (entry.isFile && 'file' in entry) {
                const file = await this.read<File>(
                    (resolve, reject) =>
                        (entry as FileSystemFileEntry).file(resolve, reject),
                    options.signal
                );
                const relativePath = entry.fullPath.replace(/^\/+/, '').trim();
                if (relativePath) {
                    Object.defineProperty(file, 'webkitRelativePath', {
                        value: relativePath,
                        configurable: true,
                    });
                }
                append(file);
            } else {
                throw new SelectionError('read');
            }
        };
        if (entries.some((item) => item.entry || item.file)) {
            for (const item of entries) {
                if (item.entry) await visit(item.entry);
                else if (item.file) append(item.file);
                else if (item.kind === 'file') throw new SelectionError('read');
            }
        } else {
            for (const file of fallbackFiles) append(file);
        }
        return files;
    }

    private async read<T>(
        start: (resolve: (value: T) => void, reject: () => void) => void,
        signal?: AbortSignal
    ): Promise<T> {
        const result = await new Promise<
            | { kind: 'value'; value: T }
            | { kind: 'error'; reason: 'read' | 'aborted' }
        >((resolve) => {
            const abort = () => {
                cleanup();
                resolve({ kind: 'error', reason: 'aborted' });
            };
            const cleanup = () => signal?.removeEventListener('abort', abort);
            if (signal?.aborted) {
                abort();
                return;
            }
            signal?.addEventListener('abort', abort, { once: true });
            try {
                start(
                    (value) => {
                        cleanup();
                        resolve({ kind: 'value', value });
                    },
                    () => {
                        cleanup();
                        resolve({ kind: 'error', reason: 'read' });
                    }
                );
            } catch {
                cleanup();
                resolve({ kind: 'error', reason: 'read' });
            }
        });
        if (result.kind === 'error') throw new SelectionError(result.reason);
        return result.value;
    }

    private checkAborted(signal?: AbortSignal): void {
        if (signal?.aborted) throw new SelectionError('aborted');
    }
}
